import React, {useEffect, useState} from 'react';
import {CustomModelRequests} from "../../api/requests/CustomModelRequests";
import {
    Alert,
    CircularProgress,
    Divider,
    Paper,
    Stack,
    SxProps,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from "@mui/material";
import {CustomModelPrecheckJson} from "../../api/json/CustomModelPrecheckJson";
import {ProjectJson} from "../../api/json/ProjectJson";
import {AnyCustomModelOptions} from "../../api/json/CustomModelOptionsJson";
import ApiError from "../../api/ApiError"
import {PublicStatsJson, PublicStatsRequests} from "../../api/requests/PublicStatsRequests"
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {ModelPlatform} from "../../api/enum/ModelPlatform"
import animations from "../../Animations.module.scss"
import {AnyTemplateJson} from "../../api/json/TemplateJson"
import {TemplateType} from "../../api/enum/TemplateType"
import ErrorMessage from "../error/ErrorMessage"

interface PrecheckPanelProps {
    project: ProjectJson
    options: AnyCustomModelOptions
    platform: ModelPlatform
    template: AnyTemplateJson
    setBlocked: (blocked: boolean) => void
    sx?: SxProps
}

interface PrecheckIssue {
    content: string
    severity: "warning" | "error"
}

function PrecheckPanel({project, options, platform, template, setBlocked, sx}: PrecheckPanelProps) {
    const [results, setResults] = useState<CustomModelPrecheckJson>()
    const [issues, setIssues] = useState<PrecheckIssue[]>()
    const [systemStats, setSystemStats] = useState<PublicStatsJson>()
    const [error, setError] = useState<ApiError>()

    useEffect(() => {
        CustomModelRequests.precheck(project.id, "", {...options, templateId: template.id})
            .then(results => {
                setResults(results)
                setError(undefined)
            })
            .catch(setError)
    }, [project, template, options])


    useEffect(() => {
        PublicStatsRequests.getStats()
            .then(setSystemStats)
            .catch(setError)
    }, [])

    useEffect(() => {
        if (!results || options.platform !== platform) return

        const newIssues: PrecheckIssue[] = []


        if (options.platform === ModelPlatform.OPEN_AI && project.stats.numberOfTrainingRows < 10)
            newIssues.push({content: `OpenAI requires at least 10 rows to fine-tune.`, severity: "error"})

        if (results.trainingRowsCount === 0)
            newIssues.push({content: `No new rows were added since this model was trained.`, severity: "error"})

        if (template.type === TemplateType.STANDARD && options.baseModel?.syntax !== null) {
            newIssues.push({content: `Standard template not recommended for this model`, severity: "warning"})
        }

        if (template.type === TemplateType.CHAT && options.baseModel!.syntax === null) {
            newIssues.push({content: `Chat template is incompatible with this model.`, severity: "error"})
        }

        const isBlocked = newIssues.filter(issue => issue.severity === "error").length > 0
        setBlocked(isBlocked)
        setIssues(newIssues)
    }, [results])

    if (error) {
        return <ErrorMessage error={error}/>
    }

    if (!results) return <CircularProgress/>

    const trainingMinutes = systemStats?.customModelTimes.find(it => it.platform.toString() === platform.toString())?.minutes
    let timeEstimate = ""
    if (trainingMinutes) {
        if (trainingMinutes > 60) {
            timeEstimate = `${Math.round(trainingMinutes / 120) * 2} hours`
        } else {
            timeEstimate = `${trainingMinutes} minutes`
        }
    }

    if (platform !== options.platform) {
        return <></>
    }

    return (
        <Stack spacing={2} className={animations.defaultIn} sx={sx}>
            <Paper variant="outlined" sx={{p: 2}}>
                <Stack spacing={1}>
                    <TableContainer sx={{boxShadow: "none"}}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{paddingLeft: "0 !important"}}>
                                        <Typography variant="subtitle1">
                                            Dataset
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{paddingRight: "0 !important"}}>
                                        <Typography variant="subtitle1">
                                            Rows
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                <TableRow>
                                    <TableCell sx={{paddingLeft: "0 !important"}}>
                                        <Typography variant="body1">Training</Typography>
                                    </TableCell>
                                    <TableCell sx={{paddingRight: "0 !important"}}>
                                        <Typography variant="body1">{results.trainingRowsCount.toLocaleString()}</Typography>
                                    </TableCell>
                                </TableRow>
                                {"baseModelName" in options &&
                                    <TableRow>
                                        <TableCell sx={{paddingLeft: "0 !important"}}>
                                            <Typography variant="body1">Validation</Typography>
                                        </TableCell>
                                        <TableCell sx={{paddingRight: "0 !important"}}>
                                            <Typography variant="body1">
                                                {results.validationRowsCount.toLocaleString()}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                }
                                <TableRow>
                                    <TableCell sx={{paddingLeft: "0 !important"}}>
                                        <Typography variant="body1">
                                            Total
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body1">{(results.trainingRowsCount + results.validationRowsCount).toLocaleString()}</Typography>
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Stack>
            </Paper>

            {timeEstimate && (
                <Paper variant="outlined" sx={{p: 2}}>
                    <Stack spacing={1}>
                        <Typography variant="subtitle1">Estimate</Typography>
                        <Stack direction="row" spacing={0.75} alignItems="center">
                            <Typography variant="body1">
                                Time: {timeEstimate}
                            </Typography>
                            <Tooltip title="Based on recent fine-tunes for this platform">
                                <InfoOutlinedIcon color="secondary" sx={{fontSize: 17}}/>
                            </Tooltip>
                        </Stack>
                        <Divider sx={{width: 60}}/>
                        <Typography variant="caption">Actual times may vary. You are responsible for all charges.
                            Validation rows are evaluated after fine-tuning and incur additional charges.</Typography>
                    </Stack>
                </Paper>
            )}

            {issues?.map((issue, index) => <Alert key={index} severity={issue.severity}>{issue.content}</Alert>)}
        </Stack>
    );
}

export default PrecheckPanel;
